import { Box } from '@factorialco/deprecated-design-system'
import { F0Box, F0Text } from '@factorialco/f0-react'

import type {
  PoFieldDefinitionDetailFragment,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import MoneyWithCurrency from 'modules/core/lib/money'

import type { LineItemDraft } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'
import { formatLineItemCellValue } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/utils'

// ─── Props ──────────────────────────────────────────────────────────────

interface RowProps {
  lineItem: PurchaseOrderDetailFieldsFragment['poLineItems'][number]
  rowIndex: number
  columnDefinitions: PoFieldDefinitionDetailFragment[]
  gridStyle: Partial<CSSStyleDeclaration>
  locale: string
  currency: string
}

interface DraftRowProps {
  draft: LineItemDraft
  columnDefinitions: PoFieldDefinitionDetailFragment[]
  gridStyle: Partial<CSSStyleDeclaration>
  locale: string
  currency: string
}

// ─── Read-only row (from server data) ───────────────────────────────────

function Row({ lineItem, rowIndex, columnDefinitions, gridStyle, locale, currency }: RowProps) {
  const valueByDefId = new Map<string, string | null>()
  const displayValueByDefId = new Map<string, string | null>()
  for (const fv of lineItem.poFieldValues) {
    if (!fv.fieldDefinition) continue
    valueByDefId.set(fv.fieldDefinition.id, fv.value ?? null)
    displayValueByDefId.set(fv.fieldDefinition.id, fv.displayValue ?? null)
  }

  return (
    <Box style={gridStyle}>
      {columnDefinitions.map((def) => {
        let cellContent: string
        if (def.fieldKey === 'line_number') {
          cellContent = `#${rowIndex + 1}`
        } else {
          const rawValue = valueByDefId.get(def.id) ?? null
          const dv = displayValueByDefId.get(def.id) ?? undefined
          cellContent = formatLineItemCellValue(
            rawValue,
            def,
            locale,
            currency,
            MoneyWithCurrency,
            dv
          )
        }
        return (
          <F0Box key={def.id} paddingX="sm" paddingY="sm">
            <F0Text content={cellContent} variant="body" />
          </F0Box>
        )
      })}
    </Box>
  )
}

// ─── Draft row (from local edit state) ──────────────────────────────────

function DraftRow({ draft, columnDefinitions, gridStyle, locale, currency }: DraftRowProps) {
  const valueByDefId = new Map<string, string>()
  const displayValueByDefId = new Map<string, string | undefined>()
  for (const fv of draft.fieldValues) {
    valueByDefId.set(fv.fieldDefinitionId, fv.value)
    displayValueByDefId.set(fv.fieldDefinitionId, fv.displayValue)
  }

  return (
    <Box style={gridStyle}>
      {columnDefinitions.map((def) => {
        let cellContent: string
        if (def.fieldKey === 'line_number') {
          cellContent = `#${draft.position}`
        } else {
          const rawValue = valueByDefId.get(def.id) ?? null
          const dv = displayValueByDefId.get(def.id)
          cellContent = formatLineItemCellValue(
            rawValue,
            def,
            locale,
            currency,
            MoneyWithCurrency,
            dv
          )
        }
        return (
          <F0Box key={def.id} paddingX="sm" paddingY="sm">
            <F0Text content={cellContent} variant="body" />
          </F0Box>
        )
      })}
    </Box>
  )
}

export { Row, DraftRow }
